// Training system with real database integration
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { 
  BookOpen, 
  PlayCircle, 
  Award, 
  Calendar, 
  Users, 
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertCircle,
  Star,
  BarChart3,
  Plus,
  ArrowLeft,
  Edit
} from "lucide-react";
import { Settings } from "lucide-react";
import { toast } from "sonner";
import { ModuleForm } from "./components/ModuleForm";

interface TrainingModule {
  id: string;
  title_en: string;
  title_el: string;
  description_en?: string;
  description_el?: string;
  content_en?: any;
  content_el?: any;
  duration_minutes?: number;
  difficulty_level?: string;
  category?: string;
  category_id?: string;
  is_active?: boolean;
  tags?: string[];
  training_categories?: TrainingCategory;
}

interface TrainingCategory {
  id: string;
  name_en: string;
  name_el: string;
  description_en?: string;
  description_el?: string;
  color: string;
  icon: string;
}

const TrainingDashboard = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { user } = useSupabaseUser();
  const { isAdmin, isLoading: isLoadingRoles } = useUserRoles();
  const [modules, setModules] = useState<TrainingModule[]>([]);
  const [completions, setCompletions] = useState<any[]>([]);
  const [categories, setCategories] = useState<TrainingCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModuleDialogOpen, setIsModuleDialogOpen] = useState(false);
  const [editingModule, setEditingModule] = useState<TrainingModule | null>(null);

  useEffect(() => {
    // Wait for user to load and roles to finish loading completely
    if (user && !isLoadingRoles) {
      // Add a small delay to ensure roles are fully settled after cache/fresh data fetch
      const timer = setTimeout(() => {
        console.log('User and roles loaded, fetching data. User ID:', user.id, 'isAdmin:', isAdmin);
        fetchData();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [user, isLoadingRoles, isAdmin]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      console.log('fetchData called - User:', user?.id, 'isAdmin:', isAdmin, 'isLoadingRoles:', isLoadingRoles);
      
      // Ensure we have both user and roles loaded
      if (!user || isLoadingRoles) {
        console.log('User or roles not ready, aborting fetch');
        setLoading(false);
        return;
      }
      
      // Fetch categories if admin
      if (isAdmin) {
        const { data: categoriesData, error: categoriesError } = await supabase
          .from('training_categories')
          .select('*')
          .order('name_en');

        if (categoriesError) throw categoriesError;
        setCategories(categoriesData || []);
      }
      
      // If admin, fetch modules based on role
      if (isAdmin) {
        console.log('Fetching modules for admin');
        
        // Check if super admin
        const { data: isSuperAdminData } = await supabase.rpc('is_super_admin', { user_id: user.id });
        const isSuperAdmin = isSuperAdminData === true;
        
        let query = supabase
          .from('training_modules')
          .select(`
            *,
            training_categories (
              id,
              name_en,
              name_el,
              color,
              icon
            )
          `)
          .eq('is_active', true);
        
        // If not super admin, only show modules in their bubble
        if (!isSuperAdmin) {
          // Get all admins in this admin's bubble (contributors they created)
          const { data: contributors } = await supabase
            .from('admin_contributors')
            .select('contributor_admin_id')
            .eq('parent_admin_id', user.id);
          
          const bubbleAdminIds = [user.id, ...(contributors?.map(c => c.contributor_admin_id) || [])];
          console.log('Admin bubble IDs:', bubbleAdminIds);
          
          query = query.in('created_by', bubbleAdminIds);
        }
        
        const { data: modulesData, error: modulesError } = await query.order('created_at', { ascending: false });

        if (modulesError) throw modulesError;
        console.log('Admin modules fetched:', modulesData?.length || 0);
        setModules(modulesData || []);
      } else {
        console.log('Fetching assigned modules for regular user');
        
        // First get the user's teams
        const { data: userTeams, error: teamsError } = await supabase
          .from('team_members')
          .select('team_id')
          .eq('user_id', user.id)
          .eq('is_active', true);
        
        if (teamsError) {
          console.error('Error fetching user teams:', teamsError);
        }
        
        const teamIds = userTeams?.map(t => t.team_id) || [];
        console.log('User team IDs:', teamIds);
        
        // Get assignments for user's teams
        let assignmentQuery = supabase
          .from('training_module_assignments')
          .select('module_id, team_id');
        
        if (teamIds.length > 0) {
          // Get assignments where team_id is in user's teams
          assignmentQuery = assignmentQuery.in('team_id', teamIds);
        } else {
          // No teams, return empty
          console.log('User is not a member of any teams');
          setModules([]);
          return;
        }
        
        const { data: assignments, error: assignmentError } = await assignmentQuery;
        console.log('Team assignments found:', assignments);
        
        if (assignmentError) {
          console.error('Error fetching assignments:', assignmentError);
          setModules([]);
          return;
        }
        
        const assignedModuleIds = assignments?.map(a => a.module_id) || [];
        
        if (assignedModuleIds.length === 0) {
          console.log('No modules assigned to user');
          setModules([]);
          return;
        }
        
        // Now get the actual modules
        const { data: assignedModules, error: modulesError } = await supabase
          .from('training_modules')
          .select(`
            *,
            training_categories (
              id,
              name_en,
              name_el,
              color,
              icon
            )
          `)
          .eq('is_active', true)
          .in('id', assignedModuleIds)
          .order('created_at', { ascending: false });

        if (modulesError) {
          console.error('Error fetching assigned modules:', modulesError);
          setModules([]);
        } else {
          console.log('Assigned modules fetched:', assignedModules?.length || 0);
          setModules(assignedModules || []);
        }
      }

      // Fetch user progress to show stats and scores
      if (!isAdmin) {
        const { data: userProgress, error: progressError } = await supabase
          .from('user_module_progress')
          .select('*')
          .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

        if (progressError) throw progressError;
        setCompletions(userProgress || []);
      }

    } catch (error) {
      console.error('Error fetching training data:', error);
      toast.error('Failed to load training data');
    } finally {
      setLoading(false);
    }
  };

  const getUserTeamIds = async () => {
    try {
      const { data: userTeams, error } = await supabase
        .from('team_members')
        .select('team_id')
        .eq('profile_id', (await supabase.auth.getUser()).data.user?.id);
      
      if (error) throw error;
      return userTeams?.map(t => t.team_id).join(',') || '';
    } catch (error) {
      console.error('Error fetching user teams:', error);
      return '';
    }
  };

  const getModuleProgress = (moduleId: string) => {
    return completions.find((c: any) => c.module_id === moduleId);
  };

  const addModule = () => {
    setEditingModule({
      id: '',
      title_en: '',
      title_el: '',
      description_en: '',
      description_el: '',
      category_id: '',
      content_en: '',
      content_el: '',
      duration_minutes: 30,
      difficulty_level: 'beginner',
      is_active: true,
      tags: []
    });
    setIsModuleDialogOpen(true);
  };

  const editModule = (module: TrainingModule) => {
    setEditingModule(module);
    setIsModuleDialogOpen(true);
  };

  const saveModule = async () => {
    if (!editingModule) return;

    try {
      if (editingModule.id) {
        // Update existing module
        const { error } = await supabase
          .from('training_modules')
          .update({
            title_en: editingModule.title_en,
            title_el: editingModule.title_el,
            description_en: editingModule.description_en,
            description_el: editingModule.description_el,
            category_id: editingModule.category_id || null,
            content_en: editingModule.content_en,
            content_el: editingModule.content_el,
            duration_minutes: editingModule.duration_minutes,
            difficulty_level: editingModule.difficulty_level,
            is_active: editingModule.is_active,
            tags: editingModule.tags
          })
          .eq('id', editingModule.id);

        if (error) throw error;
      } else {
        // Create new module
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const { error } = await supabase
          .from('training_modules')
          .insert({
            title_en: editingModule.title_en,
            title_el: editingModule.title_el,
            description_en: editingModule.description_en,
            description_el: editingModule.description_el,
            category_id: editingModule.category_id || null,
            content_en: editingModule.content_en,
            content_el: editingModule.content_el,
            duration_minutes: editingModule.duration_minutes,
            difficulty_level: editingModule.difficulty_level,
            is_active: editingModule.is_active,
            tags: editingModule.tags,
            created_by: user.id,
            created_by_admin: user.id
          });

        if (error) throw error;
      }

      toast.success('Module saved successfully!');
      setIsModuleDialogOpen(false);
      setEditingModule(null);
      fetchData();
    } catch (error) {
      console.error('Error saving module:', error);
      toast.error('Failed to save module');
    }
  };

  const getDifficultyBadge = (level: string) => {
    const variants = {
      beginner: { label: language === 'en' ? 'Beginner' : 'Αρχάριος', color: 'bg-green-100 text-green-800' },
      intermediate: { label: language === 'en' ? 'Intermediate' : 'Μέσο', color: 'bg-yellow-100 text-yellow-800' },
      advanced: { label: language === 'en' ? 'Advanced' : 'Προχωρημένο', color: 'bg-orange-100 text-orange-800' }
    };
    const variant = variants[level as keyof typeof variants] || variants.beginner;
    return <Badge className={variant.color}>{variant.label}</Badge>;
  };

  if (loading || isLoadingRoles || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t('loadingTrainingModules')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 relative overflow-hidden">
      {/* Enhanced background patterns */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/3 via-transparent to-blue-500/3 pointer-events-none"></div>
      <div className="absolute top-20 right-10 w-96 h-96 bg-gradient-radial from-blue-500/8 to-transparent rounded-full blur-3xl pointer-events-none animate-pulse"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-gradient-radial from-primary/8 to-transparent rounded-full blur-3xl pointer-events-none animate-pulse" style={{ animationDelay: '2s' }}></div>
      
      <div className="relative z-10 container mx-auto p-6 space-y-8 animate-fade-in">
        {/* Enhanced Header Section */}
        <div className="bg-gradient-to-r from-card/80 via-card/60 to-card/40 backdrop-blur-sm rounded-2xl border border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 p-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                {language === 'en' ? 'Training Management' : 'Διαχείριση Εκπαίδευσης'}
              </h1>
              <p className="text-muted-foreground text-lg mt-2">
                {language === 'en' 
                  ? 'Master essential security skills through our comprehensive training modules'
                  : 'Κατακτήστε βασικές δεξιότητες ασφαλείας μέσω των ολοκληρωμένων εκπαιδευτικών μας μοντέλων'
                }
              </p>
            </div>
          </div>

          {/* Navigation buttons */}
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => navigate('/dashboard')} className="hover-scale border-primary/20 hover:border-primary/40 hover:bg-primary/5">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {language === 'en' ? 'Back to Dashboard' : 'Επιστροφή στον Πίνακα Ελέγχου'}
            </Button>
            <Button variant="outline" onClick={() => navigate('/training/sessions')} className="hover-scale border-primary/20 hover:border-primary/40 hover:bg-primary/5">
              <Calendar className="h-4 w-4 mr-2" />
              {language === 'en' ? 'Scheduled Sessions' : 'Προγραμματισμένες Συνεδρίες'}
            </Button>
            <Button variant="outline" onClick={() => navigate('/training/certificates')} className="hover-scale border-primary/20 hover:border-primary/40 hover:bg-primary/5">
              <Award className="h-4 w-4 mr-2" />
              {language === 'en' ? 'My Certificates' : 'Τα Πιστοποιητικά μου'}
            </Button>
            {isAdmin && (
              <>
                <Dialog open={isModuleDialogOpen} onOpenChange={setIsModuleDialogOpen}>
                  <DialogTrigger asChild>
                    <Button onClick={addModule} className="hover-scale bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 shadow-md hover:shadow-lg">
                      <Plus className="h-4 w-4 mr-2" />
                      {language === 'en' ? 'Create Module' : 'Δημιουργία Μοντέλου'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>
                        {editingModule?.id 
                          ? (language === 'en' ? 'Edit Training Module' : 'Επεξεργασία Εκπαιδευτικού Μοντέλου')
                          : (language === 'en' ? 'Create Training Module' : 'Δημιουργία Εκπαιδευτικού Μοντέλου')
                        }
                      </DialogTitle>
                    </DialogHeader>
                    <ModuleForm 
                      module={editingModule}
                      categories={categories}
                      onSubmit={saveModule}
                      onCancel={() => setIsModuleDialogOpen(false)}
                      onChange={(module) => setEditingModule(module)}
                    />
                  </DialogContent>
                </Dialog>
                <Button variant="outline" onClick={() => navigate('/training/admin')} className="hover-scale border-primary/20 hover:border-primary/40 hover:bg-primary/5">
                  <Users className="h-4 w-4 mr-2" />
                  {language === 'en' ? 'Admin Panel' : 'Πάνελ Διαχειριστή'}
                </Button>
                <Button variant="outline" onClick={() => navigate('/training/analytics')} className="hover-scale border-primary/20 hover:border-primary/40 hover:bg-primary/5">
                  <BarChart3 className="h-4 w-4 mr-2" />
                  {language === 'en' ? 'Analytics' : 'Αναλυτικά'}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Enhanced Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="bg-gradient-to-br from-card via-card/90 to-blue-50/30 border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 hover-scale">
            <CardContent className="flex items-center p-6">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mr-4">
                <BookOpen className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {language === 'en' ? 'Total Modules' : 'Συνολικά Μοντέλα'}
                </p>
                <p className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">{modules.length}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-card via-card/90 to-green-50/30 border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 hover-scale">
            <CardContent className="flex items-center p-6">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center mr-4">
                <CheckCircle2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {language === 'en' ? 'Completed' : 'Ολοκληρωμένα'}
                </p>
                <p className="text-3xl font-bold bg-gradient-to-r from-green-600 to-green-500 bg-clip-text text-transparent">
                  {completions.filter((c: any) => c.progress_percentage === 100).length}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-card via-card/90 to-orange-50/30 border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 hover-scale">
            <CardContent className="flex items-center p-6">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center mr-4">
                <Clock className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {language === 'en' ? 'In Progress' : 'Σε Εξέλιξη'}
                </p>
                <p className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-orange-500 bg-clip-text text-transparent">
                  {Math.max(0, modules.length - completions.filter((c: any) => c.progress_percentage === 100).length)}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-card via-card/90 to-purple-50/30 border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 hover-scale">
            <CardContent className="flex items-center p-6">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center mr-4">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {language === 'en' ? 'Completion Rate' : 'Ποσοστό Ολοκλήρωσης'}
                </p>
                <p className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-purple-500 bg-clip-text text-transparent">
                  {modules.length > 0 ? Math.round((completions.filter((c: any) => c.progress_percentage === 100).length / modules.length) * 100) : 0}%
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Enhanced Training Modules */}
        <div className="bg-gradient-to-r from-card/90 via-card/70 to-card/50 backdrop-blur-xl border border-border/50 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-primary-foreground" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              {language === 'en' ? 'Training Categories' : 'Κατηγορίες Εκπαίδευσης'}
            </h2>
          </div>

          <Tabs defaultValue="all" className="w-full">
            <TabsList className="grid w-full grid-cols-5 bg-muted/50 backdrop-blur-sm">
              <TabsTrigger value="all" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">{language === 'en' ? 'All' : 'Όλα'}</TabsTrigger>
              <TabsTrigger value="security" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">{language === 'en' ? 'Security' : 'Ασφάλεια'}</TabsTrigger>
              <TabsTrigger value="technology" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">{language === 'en' ? 'Technology' : 'Τεχνολογία'}</TabsTrigger>
              <TabsTrigger value="emergency" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">{language === 'en' ? 'Emergency' : 'Έκτακτη Ανάγκη'}</TabsTrigger>
              <TabsTrigger value="access_control" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">{language === 'en' ? 'Access Control' : 'Έλεγχος Πρόσβασης'}</TabsTrigger>
            </TabsList>

            {['all', 'security', 'technology', 'emergency', 'access_control'].map(category => (
              <TabsContent key={category} value={category} className="space-y-6 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {modules
                    .filter(module => {
                      if (category === 'all') return true;
                      // Check if module has a training_categories relation with matching name
                      if (module.training_categories) {
                        const categoryName = language === 'en' 
                          ? module.training_categories.name_en?.toLowerCase()
                          : module.training_categories.name_el?.toLowerCase();
                        return categoryName === category || categoryName?.includes(category.replace('_', ' '));
                      }
                      // Fallback to category field if no relation
                      return module.category === category;
                    })
                    .map((module, index) => {
                      const progress = getModuleProgress(module.id);
                      const isCompleted = progress && progress.progress_percentage === 100;
                      const score = progress?.final_score;
                      
                      // Debug logging
                      if (isCompleted) {
                        console.log('Module:', module.title_en, 'Progress:', progress, 'Score:', score);
                      }
                      
                      return (
                        <Card 
                          key={module.id} 
                          className="bg-gradient-to-br from-card via-card/90 to-muted/30 border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 hover-scale cursor-pointer group animate-fade-in"
                          style={{ animationDelay: `${index * 100}ms` }}
                        >
                          <CardHeader className="pb-3">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start space-x-3 flex-1 min-w-0">
                                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center group-hover:from-primary/30 group-hover:to-primary/20 transition-all flex-shrink-0">
                                  {isCompleted ? (
                                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                                  ) : (
                                    <PlayCircle className="h-5 w-5 text-primary" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0 space-y-1">
                                  <CardTitle className="text-lg leading-tight text-foreground group-hover:text-primary transition-colors">
                                    {language === 'en' ? module.title_en : module.title_el}
                                  </CardTitle>
                                  {module.training_categories && (
                                    <p className="text-xs text-muted-foreground">
                                      {language === 'en' ? module.training_categories.name_en : module.training_categories.name_el}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-start gap-1.5 flex-shrink-0">
                                {isCompleted && score !== null && score !== undefined && (
                                  <Badge className="bg-green-500 hover:bg-green-600 text-white">
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    {score}%
                                  </Badge>
                                )}
                                {getDifficultyBadge(module.difficulty_level)}
                                {isAdmin && (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 hover:bg-primary/10"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/training/module/${module.id}?admin=true`);
                                      }}
                                      title="Open Admin Editor"
                                    >
                                      <Settings className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 hover:bg-primary/10"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        editModule(module);
                                      }}
                                      title="Edit Module"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          
                          <CardDescription className="text-sm line-clamp-2 text-muted-foreground mt-2">
                            {language === 'en' ? module.description_en : module.description_el}
                          </CardDescription>
                        </CardHeader>

                        <CardContent className="pt-0">
                          <div className="space-y-4">
                            <div className="flex items-center justify-between text-sm text-muted-foreground">
                              <span className="flex items-center">
                                <Clock className="h-4 w-4 mr-2" />
                                {module.duration_minutes} min
                              </span>
                            </div>

                            <Button 
                              className={isCompleted 
                                ? "w-full hover-scale bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 shadow-md hover:shadow-lg text-white"
                                : "w-full hover-scale bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 shadow-md hover:shadow-lg"
                              }
                              variant="default"
                              size="sm"
                              onClick={() => navigate(`/training/module/${module.id}`)}
                            >
                              {isCompleted ? (
                                <>
                                  <CheckCircle2 className="h-4 w-4 mr-2" />
                                  {language === 'en' ? 'Completed' : 'Ολοκληρώθηκε'}
                                </>
                              ) : (
                                <>
                                  <PlayCircle className="h-4 w-4 mr-2" />
                                  {language === 'en' ? 'Start Training' : 'Έναρξη Εκπαίδευσης'}
                                </>
                              )}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                      );
                    })}
                </div>
                
                {/* Empty state for categories with no modules */}
                {modules.filter(module => {
                  if (category === 'all') return true;
                  if (module.training_categories) {
                    const categoryName = language === 'en' 
                      ? module.training_categories.name_en?.toLowerCase()
                      : module.training_categories.name_el?.toLowerCase();
                    return categoryName === category || categoryName?.includes(category.replace('_', ' '));
                  }
                  return module.category === category;
                }).length === 0 && (
                  <div className="text-center py-12">
                    <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-muted to-muted/70 flex items-center justify-center mb-4">
                      <BookOpen className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {language === 'en' ? 'No modules in this category' : 'Δεν υπάρχουν μοντέλα σε αυτή την κατηγορία'}
                    </h3>
                    <p className="text-muted-foreground">
                      {language === 'en' ? 'Check other categories or create new modules.' : 'Ελέγξτε άλλες κατηγορίες ή δημιουργήστε νέα μοντέλα.'}
                    </p>
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default TrainingDashboard;